import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { api } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'

const MOCK_AUTH = import.meta.env.VITE_MOCK_AUTH === 'true'
const isLocalDev =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

export type UserRole = 'admin' | 'deployer' | 'viewer' | 'user'

// eslint-disable-next-line react-refresh/only-export-components
export const ROLE_LABELS: Record<UserRole, string> = {
  admin:    'Super Admin',
  deployer: 'Admin',
  viewer:   'Viewer',
  user:     'Standard User',
}

interface UserRolesContextValue {
  loading: boolean
  error: string | null
  initialized: boolean
  role: UserRole
  isAdmin: boolean
  isDeployer: boolean
  isViewer: boolean
  userProducts: string[]
  rolesEnforced: boolean
  isRegisteredUser: boolean
  hasAccess: (product: string) => boolean
  canDeploy: (product: string) => boolean
  canManageRunbooks: (product: string) => boolean
  accessibleProducts: string[] | null
  refreshRoles: () => Promise<{ role: UserRole; isAdmin: boolean; products: string[] }>
}

const UserRolesContext = createContext<UserRolesContextValue | null>(null)

export function UserRolesProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuthStore()
  const currentUserEmail = user?.email?.trim() || null

  const devMode = isLocalDev || MOCK_AUTH
  const [role, setRole] = useState<UserRole>(devMode ? 'admin' : 'user')
  const [userProducts, setUserProducts] = useState<string[]>([])
  const [anyRolesExist, setAnyRolesExist] = useState(!devMode)
  const [loading, setLoading] = useState(!devMode)
  const [error, setError] = useState<string | null>(null)
  const [initialized, setInitialized] = useState(devMode)

  useEffect(() => {
    if (devMode) return
    if (!isAuthenticated) return
    // Authenticated but no email (edge case: empty email in auth backend)
    if (!currentUserEmail) {
      setLoading(false)
      setInitialized(true)
      return
    }

    const fetchRoles = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await api.get(`/auth/user-roles/user/${encodeURIComponent(currentUserEmail)}/`)
        const data = res.data
        setRole((data.role as UserRole) || 'user')
        setUserProducts(data.products || [])

        try {
          const allRes = await api.get('/auth/user-roles/')
          const allRoles: unknown[] = allRes.data
          setAnyRolesExist(allRoles.length > 0)
        } catch {
          // Keep anyRolesExist = true (safe default)
        }
      } catch (err: unknown) {
        const e = err as { response?: { status: number } }
        if (e?.response?.status === 500 || e?.response?.status === 401) {
          setRole('user')
          setUserProducts([])
          setAnyRolesExist(false)
        } else {
          setError('Failed to fetch user roles')
        }
      } finally {
        setLoading(false)
        setInitialized(true)
      }
    }

    fetchRoles()
  }, [currentUserEmail, devMode, isAuthenticated])

  const refreshRoles = useCallback(async () => {
    if (!currentUserEmail) return { role: 'user' as UserRole, isAdmin: false, products: [] }
    setLoading(true)
    try {
      const res = await api.get(`/auth/user-roles/user/${encodeURIComponent(currentUserEmail)}/`)
      const data = res.data
      const r = (data.role as UserRole) || 'user'
      setRole(r)
      setUserProducts(data.products || [])
      return { role: r, isAdmin: r === 'admin', products: data.products || [] }
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
    return { role, isAdmin: role === 'admin', products: userProducts }
  }, [currentUserEmail, role, userProducts])

  const isAdmin    = role === 'admin'
  const isDeployer = role === 'deployer'
  const isViewer   = role === 'viewer'

  const isRegisteredUser = useMemo(() => {
    if (!initialized || loading) return true
    if (!anyRolesExist) return true
    if (devMode) return true
    if (isAdmin || isDeployer || isViewer) return true
    if (userProducts.length > 0) return true
    return false
  }, [initialized, loading, anyRolesExist, devMode, isAdmin, isDeployer, isViewer, userProducts])

  const hasAccess = useCallback((productName: string) => {
    if (!initialized || loading) return false
    if (isAdmin || isDeployer) return true
    if (isViewer) return false
    if (userProducts.length > 0) return userProducts.some(p => p.toLowerCase() === productName.toLowerCase())
    if (!anyRolesExist) return true
    return false
  }, [isAdmin, isDeployer, isViewer, userProducts, anyRolesExist, initialized, loading])

  const canDeploy = useCallback((product: string) => {
    if (!initialized || loading) return false
    if (isAdmin || isDeployer) return true
    // Team product access takes priority — check before anyRolesExist fallback
    if (userProducts.length > 0) {
      return userProducts.some((p) => p.toLowerCase() === product.toLowerCase())
    }
    // No team products: allow only if platform has no RBAC configured
    if (!anyRolesExist) return true
    return false
  }, [isAdmin, isDeployer, anyRolesExist, userProducts, initialized, loading])

  const canManageRunbooks = useCallback((productName: string) => {
    if (!initialized || loading) return false
    if (isViewer) return false
    if (isAdmin || isDeployer) return true
    if (!anyRolesExist) return true
    return hasAccess(productName)
  }, [isAdmin, isDeployer, isViewer, anyRolesExist, initialized, loading, hasAccess])

  const rolesEnforced = useMemo(() => anyRolesExist, [anyRolesExist])

  const accessibleProducts = useMemo(() => {
    if (isAdmin || isDeployer) return null
    if (isViewer) return []
    return userProducts
  }, [isAdmin, isDeployer, isViewer, userProducts])

  const value: UserRolesContextValue = {
    loading, error, initialized,
    role, isAdmin, isDeployer, isViewer, userProducts,
    rolesEnforced, isRegisteredUser,
    hasAccess, canDeploy, canManageRunbooks, accessibleProducts,
    refreshRoles,
  }

  return (
    <UserRolesContext.Provider value={value}>
      {children}
    </UserRolesContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUserRoles() {
  const ctx = useContext(UserRolesContext)
  if (!ctx) throw new Error('useUserRoles must be used within UserRolesProvider')
  return ctx
}

export default UserRolesContext
